import { Spinner, Text } from '@risksmart-app/atomic-ui';
import { AlertTriangle, CheckCircle } from '@untitled-ui/icons-react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetThirdPartyPortalStatus } from 'src/hooks/queries/third-party-portal/useGetThirdPartyPortalStatus';
import { useIsCustomerSupport } from 'src/hooks/useIsCustomerSupport';

/**
 * CS-only diagnostic panel (RSP-6736) surfaced under the Third Party module.
 *
 * A third-party contact is added to the Auth0 org at creation, but can only
 * log into the portal if the org has the `Username-Password-ThirdParty`
 * connection enabled. When it isn't, contacts are created + emailed but fail
 * login with "does not belong to any organization". This panel turns that
 * otherwise-invisible provisioning gap into a glance for CS.
 */
export const ThirdPartyPortalDiagnostics: FC = () => {
  const { t } = useTranslation('common', {
    keyPrefix: 'modules.thirdPartyPortalDiagnostics',
  });
  const isCustomerSupport = useIsCustomerSupport();
  // `shouldSkip` disables BOTH the tRPC and GraphQL queries for non-CS viewers,
  // so the (CS-gated, 403-ing) status request never fires when the component is
  // mounted for a non-CS user.
  const { data, loading, error } = useGetThirdPartyPortalStatus({
    queryArgs: {},
    shouldSkip: !isCustomerSupport,
  });

  // Defence-in-depth: the parent only mounts this for CS, but never render the
  // diagnostic for a non-CS viewer.
  if (!isCustomerSupport) {
    return null;
  }

  const status = data?.getThirdPartyPortalStatus;

  return (
    <div className={'mt-4 flex flex-col gap-2'}>
      <h4 className={'my-0 text-lg'}>{t('title')}</h4>
      <Text className={'text-grey500'}>{t('description')}</Text>
      {loading && <Spinner />}
      {error && <Text className={'text-red500'}>{t('error')}</Text>}
      {status &&
        (status.connectionEnabled ? (
          <div className={'flex items-center gap-2 text-green600'}>
            <CheckCircle width={16} />
            <Text>{t('provisioned')}</Text>
          </div>
        ) : (
          <div className={'flex flex-col gap-1'}>
            <div className={'flex items-center gap-2 text-orange600'}>
              <AlertTriangle width={16} />
              <Text>{t('notProvisioned')}</Text>
            </div>
            <Text className={'text-grey500'}>
              {t('notProvisionedDetail')} {status.connectionName}
            </Text>
          </div>
        ))}
    </div>
  );
};
